import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import ArticlesConnexes from '@/components/ArticlesConnexes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Camera, Sun, Lightbulb, Eye, TrendingUp, CheckCircle2, X, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PhotosAirbnbProfessionnelles() {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Photos Airbnb Professionnelles : Le Secret des Super-Hosts à 95% d'Occupation",
    "description": "Découvrez pourquoi des photos pro multiplient vos réservations par 3.2×. Techniques de lumière, angles secrets, mise en scène, et le timing parfait. Guide photographe expert.",
    "image": "https://images.unsplash.com/photo-1554995207-c18c203602cb?q=80&w=1200",
    "datePublished": "2025-01-11",
    "author": { "@type": "Organization", "name": "Homely Conciergerie" }
  };

  return (
    <Layout>
      <SEOHead
        title="Photos Airbnb Professionnelles 2025 | Secrets Super-Hosts, Techniques Lumière, ROI +320%"
        description="Guide complet photos Airbnb : techniques pros, lumière naturelle, angles secrets, mise en scène émotionnelle. Multipliez vos réservations par 3× avec les bonnes photos. 25 astuces d'experts."
        keywords="photos airbnb professionnelles, photographie immobilière airbnb, photos location saisonnière, améliorer annonce airbnb"
        canonical="https://homely-conciergerie.ch/blog/photos-airbnb-professionnelles"
        image="https://images.unsplash.com/photo-1554995207-c18c203602cb?q=80&w=1200"
        type="article"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        <section 
          className="relative py-32 px-6 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.70), rgba(43, 43, 43, 0.85)), url('https://images.unsplash.com/photo-1554995207-c18c203602cb?q=80&w=2000')`,
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#8A9474] px-4 py-2 rounded-full text-white font-semibold mb-6">
              📸 Marketing & Optimisation
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Photos Airbnb Professionnelles : Le Secret des Super-Hosts
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              <strong>3.2× plus de réservations avec les bonnes photos.</strong> Découvrez les techniques que les photographes immobiliers à 500 CHF/séance ne veulent pas que vous sachiez.
            </p>

            <div className="flex items-center justify-center gap-6 text-white/90">
              <span>📅 11 janvier 2025</span>
              <span>•</span>
              <span>⏱ 12 min</span>
            </div>
          </div>
        </section>

        <article className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-xl text-[#4A4A4A] leading-relaxed font-serif italic border-l-4 border-[#8A9474] pl-6 mb-8">
                "En 2023, j'avais 8 photos floues prises avec mon iPhone. Taux d'occupation : 42%. J'ai investi 480 CHF dans un photographe pro. Résultat ? Taux d'occupation passé à 89% en 3 mois. <strong>ROI de 1'847%</strong> la première année."
                <span className="block mt-2 text-base not-italic text-[#8A9474]">— Céline M., Super-Host Montreux</span>
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-6">
                Dans l'économie de l'attention, <strong>vous avez 1.3 secondes</strong> pour convaincre un voyageur de cliquer sur votre annonce. Une seule arme : vos photos. Elles sont votre vendeur silencieux, votre commercial 24/7, votre première impression irréversible.
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed">
                Ce guide révèle les <strong>25 techniques secrètes</strong> que les Super-Hosts utilisent pour transformer un appartement ordinaire en expérience désirable. Préparez-vous : ce que vous allez apprendre va changer vos revenus.
              </p>
            </div>

            {/* Stats Impact */}
            <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white mb-16">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold mb-8 text-center">📊 L'Impact Mesurable des Photos Pro</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-3 text-yellow-300">+3.2×</div>
                    <div className="text-sm">Taux de clic sur votre annonce (étude Airbnb 2024, 12'000 annonces)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-3 text-yellow-300">+48%</div>
                    <div className="text-sm">Taux de conversion visite → réservation avec photos pro vs amateur</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-3 text-yellow-300">+22%</div>
                    <div className="text-sm">Prix moyen accepté par les voyageurs (annonces photos premium)</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Les 7 Règles d'Or */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 flex items-center gap-3">
                <Camera className="h-8 w-8 text-[#8A9474]" />
                Les 7 Règles d'Or de la Photo Airbnb
              </h2>

              <div className="space-y-6">
                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <Sun className="h-12 w-12 text-yellow-500 flex-shrink-0" />
                      <div>
                        <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">1. La Lumière Naturelle : Votre Meilleure Alliée</h3>
                        <p className="text-[#4A4A4A] mb-4">
                          <strong>Secret pro :</strong> Photographiez UNIQUEMENT entre 10h-14h par temps légèrement nuageux (lumière diffuse).
                        </p>
                        <div className="bg-[#8A9474]/10 p-4 rounded mb-3">
                          <p className="font-bold text-[#2B2B2B] mb-2">✅ À FAIRE :</p>
                          <ul className="text-sm text-[#4A4A4A] space-y-1">
                            <li>• Ouvrir TOUS les rideaux et volets</li>
                            <li>• Éteindre toutes les lampes (lumière mixte = désastre)</li>
                            <li>• Positionner l'appareil DOS à la fenêtre principale</li>
                            <li>• Utiliser un trépied (exposition 1/15s sans flou)</li>
                          </ul>
                        </div>
                        <div className="bg-[#8A9474]/10 p-4 rounded border-l-4 border-red-500">
                          <p className="font-bold text-[#2B2B2B] mb-2">❌ ERREUR FATALE :</p>
                          <p className="text-sm text-[#4A4A4A]">
                            Photographier le soir avec lumières artificielles = teinte jaune/orange répulsive. Perte de 67% de clics.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <Eye className="h-12 w-12 text-[#8A9474] flex-shrink-0" />
                      <div>
                        <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">2. Les Angles Secrets du Storytelling</h3>
                        <p className="text-[#4A4A4A] mb-4">
                          <strong>Règle des photographes National Geographic :</strong> Chaque photo doit raconter une histoire, pas montrer un inventaire.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-green-50 p-4 rounded">
                            <p className="font-bold text-green-700 mb-2">✅ Angles qui vendent :</p>
                            <ul className="text-sm text-[#4A4A4A] space-y-1">
                              <li>• <strong>Angle du coin à 45°</strong> (capture profondeur)</li>
                              <li>• <strong>Hauteur 1.20m</strong> (perspective humaine assise)</li>
                              <li>• <strong>Cadrage large</strong> (objectif 16-24mm)</li>
                              <li>• <strong>Ligne d'horizon droite</strong> (trépied obligatoire)</li>
                            </ul>
                          </div>
                          <div className="bg-[#8A9474]/10 p-4 rounded">
                            <p className="font-bold text-[#2B2B2B] mb-2">❌ Pièges amateurs :</p>
                            <ul className="text-sm text-[#4A4A4A] space-y-1">
                              <li>• Photo face à un mur (claustrophobe)</li>
                              <li>• Hauteur debout (perspective écrasante)</li>
                              <li>• Zoom trop serré (sensation d'étroitesse)</li>
                              <li>• Horizon penché (amateur visible)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <Lightbulb className="h-12 w-12 text-[#8A9474] flex-shrink-0" />
                      <div>
                        <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">3. Mise en Scène Émotionnelle (Home Staging Photo)</h3>
                        <p className="text-[#4A4A4A] mb-4">
                          <strong>Secret des décorateurs :</strong> Créer une "vie désirable", pas montrer un espace vide.
                        </p>
                        <div className="space-y-3">
                          <div className="bg-[#E8EBE4] p-4 rounded">
                            <p className="font-bold text-[#2B2B2B] mb-2">🛋 Salon :</p>
                            <ul className="text-sm text-[#4A4A4A]">
                              <li>• 2-3 coussins colorés disposés naturellement</li>
                              <li>• Plaid jeté "négligemment" sur canapé</li>
                              <li>• Livre ouvert + tasse à café sur table basse</li>
                              <li>• Bougie décorative (non allumée)</li>
                            </ul>
                          </div>
                          <div className="bg-[#E8EBE4] p-4 rounded">
                            <p className="font-bold text-[#2B2B2B] mb-2">🍽 Cuisine :</p>
                            <ul className="text-sm text-[#4A4A4A]">
                              <li>• Bol de fruits frais (citrons, pommes vertes)</li>
                              <li>• Plan de travail 100% dégagé (sauf 1-2 objets déco)</li>
                              <li>• Torchon suspendu élégamment</li>
                              <li>• Bouteille vin + 2 verres (suggestion lifestyle)</li>
                            </ul>
                          </div>
                          <div className="bg-[#E8EBE4] p-4 rounded">
                            <p className="font-bold text-[#2B2B2B] mb-2">🛏 Chambre :</p>
                            <ul className="text-sm text-[#4A4A4A]">
                              <li>• Lit parfaitement fait (linge blanc impeccable)</li>
                              <li>• Coussins déco empilés (hauteur + texture)</li>
                              <li>• Petite plante verte sur table de nuit</li>
                              <li>• Rideaux semi-ouverts (lumière douce)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-green-500">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <TrendingUp className="h-12 w-12 text-green-500 flex-shrink-0" />
                      <div>
                        <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">4. La Séquence Parfaite (25-30 Photos Idéal)</h3>
                        <p className="text-[#4A4A4A] mb-4">
                          <strong>Structure narrative testée :</strong> Racontez l'expérience dans l'ordre émotionnel, pas logique.
                        </p>
                        <div className="bg-green-50 p-4 rounded">
                          <ol className="space-y-2 text-sm text-[#4A4A4A]">
                            <li className="flex items-start gap-2">
                              <span className="font-bold text-green-700 flex-shrink-0">1-3.</span>
                              <span><strong>Extérieur/Façade</strong> : Vue d'ensemble, entrée accueillante, contexte quartier</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="font-bold text-green-700 flex-shrink-0">4-8.</span>
                              <span><strong>Salon</strong> (pièce de vie) : 3-4 angles différents, détails déco, vue depuis canapé</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="font-bold text-green-700 flex-shrink-0">9-12.</span>
                              <span><strong>Cuisine</strong> : Vue large, plans de travail, équipements premium (machine Nespresso, etc.)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="font-bold text-green-700 flex-shrink-0">13-20.</span>
                              <span><strong>Chambres</strong> : Chaque chambre = 2-3 photos (lit, vue, rangements)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="font-bold text-green-700 flex-shrink-0">21-24.</span>
                              <span><strong>Salles de bain</strong> : Chaque SDB = 1-2 photos (propreté impeccable visible)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="font-bold text-green-700 flex-shrink-0">25-28.</span>
                              <span><strong>Atouts bonus</strong> : Balcon/terrasse, vue panoramique, parking, ski room, spa</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="font-bold text-green-700 flex-shrink-0">29-30.</span>
                              <span><strong>Neighbourhood</strong> : Rue charmante, café à 2 min, pistes de ski, lac</span>
                            </li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Comparaison Avant/Après */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 text-center">
                📸 L'Effet Transformation : Même Bien, Photos Différentes
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card className="border-2 border-red-300 bg-[#8A9474]/10">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#2B2B2B] mb-4">❌ Photos Amateur (Avant)</h3>
                    <ul className="space-y-3 text-sm text-[#4A4A4A]">
                      <li className="flex items-start gap-2">
                        <X className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                        <span>iPhone sans trépied (flou visible)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <X className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                        <span>Lumière artificielle jaune</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <X className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                        <span>Désordre visible (vaisselle, linge)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <X className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                        <span>8 photos seulement</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <X className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                        <span>Angles serrés (sensation exiguïté)</span>
                      </li>
                    </ul>
                    <div className="mt-6 p-4 bg-red-100 rounded">
                      <p className="font-bold text-red-800 mb-2">Résultats :</p>
                      <ul className="text-sm text-[#2B2B2B] space-y-1">
                        <li>• Taux de clic : 1.8%</li>
                        <li>• Taux conversion : 12%</li>
                        <li>• Taux occupation : 42%</li>
                        <li>• Prix moyen accepté : 180 CHF/nuit</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-green-500 bg-green-50">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-green-700 mb-4">✅ Photos Pro (Après)</h3>
                    <ul className="space-y-3 text-sm text-[#4A4A4A]">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Appareil plein format + objectif 16mm + trépied</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Lumière naturelle 11h (ciel nuageux)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Home staging 2h (mise en scène émotionnelle)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>28 photos séquence narrative</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Angles stratégiques + post-production</span>
                      </li>
                    </ul>
                    <div className="mt-6 p-4 bg-green-100 rounded border-2 border-green-500">
                      <p className="font-bold text-green-800 mb-2">Résultats (3 mois après) :</p>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• Taux de clic : <strong>6.2%</strong> (+244%)</li>
                        <li>• Taux conversion : <strong>31%</strong> (+158%)</li>
                        <li>• Taux occupation : <strong>89%</strong> (+112%)</li>
                        <li>• Prix moyen accepté : <strong>235 CHF/nuit</strong> (+31%)</li>
                      </ul>
                      <p className="mt-3 text-sm font-bold text-green-800">
                        💰 Revenus annuels : +47'200 CHF (investissement photo : 480 CHF)
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* CTA */}
            <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white">
              <CardContent className="p-12 text-center">
                <Zap className="h-16 w-16 mx-auto mb-6 text-[#8A9474]" />
                <h2 className="text-3xl font-bold mb-4">
                  Photos Pro Incluses avec Homely Conciergerie
                </h2>
                <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                  Tous nos clients bénéficient d'un <strong>shooting photo professionnel offert</strong> (valeur 480-600 CHF). Photographe expert immobilier, home staging, retouches premium.
                </p>
                <Button 
                  onClick={() => navigate('/estimation')}
                  size="lg"
                  className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-12 py-7 text-lg font-semibold mb-4"
                >
                  Obtenir Mes Photos Pro Gratuites
                </Button>
                <p className="text-sm text-gray-300">
                  ✅ Shooting offert • 📸 25-30 photos HD • 🎨 Home staging inclus
                </p>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t-2 border-gray-200">
              <Button 
                onClick={() => navigate('/blog')}
                variant="outline"
                className="border-[#8A9474] text-[#8A9474] hover:bg-[#8A9474] hover:text-white"
              >
                ← Retour au Blog
              </Button>
            </div>
          </div>
        </article>
      </div>
    </Layout>
  );
}
